/**
 * Who an area names, as one derivation (RESP2c.1).
 *
 * ## Why this exists
 *
 * Three surfaces show the same people — the table row, the detail panel
 * and the phone card — and before this each counted and formatted them
 * inline. That is how a page ends up saying "Team" in one place and
 * "Person" two inches away: the arithmetic is trivial, so it gets
 * retyped, and the third copy is the one that forgets something.
 *
 * ## What it fixes beyond formatting
 *
 * **Deduplication.** The count used to be
 * `(lead ? 1 : 0) + participant_user_ids.length`, with nothing checking
 * whether the lead was ALSO in the participants. The edit form excludes
 * them from the multi-picker, so it rarely happened — but "rarely" is
 * not "never" for data that predates that rule or arrives from anywhere
 * else, and the symptom was an area with one human being labelled
 * "Team". Everything here works from a map keyed by id.
 *
 * **Names instead of a number.** "2 Mitglieder" answers a question
 * nobody asked. Who is responsible is the entire point of the page, and
 * the API has carried the names all along —
 * `responsibility_area_service` sends a `user_picker_item` projection
 * per participant, so this needs no request of its own and adds none.
 *
 * ## What it deliberately does not do
 *
 * No sorting of its own beyond "lead first": the server's participant
 * order is the organisation's, and re-sorting alphabetically here would
 * make the row disagree with the edit form. No mutation of the arrays it
 * is given. No id in anything it returns for display.
 */

/** The most people a compact surface names before it counts the rest. */
export const VISIBLE_PEOPLE_LIMIT = 2;

function toPerson(user, { isLead }) {
  if (!user) return null;
  const id = user.id == null ? null : String(user.id);
  const label = String(user.label || "").trim();
  return {
    id,
    // Never a raw id: `user_picker_item` already falls back to the email
    // because `full_name` can be blank, so an empty label here means the
    // projection itself was empty — a dash is the honest answer.
    label: label || "—",
    isActive: user.is_active !== false,
    isLead,
  };
}

/**
 * `{people, lead, total, kind, visible, overflowCount}` for one area.
 *
 * `kind` is the badge word and nothing more — Vantio has no team model,
 * and "team" here describes a configuration that happens to name several
 * people, never an entity.
 */
export function buildAreaPeople(area) {
  const lead = toPerson(area?.default_assigned_to_user, { isLead: true });
  const participants = Array.isArray(area?.participants)
    ? area.participants
    : [];

  const people = [];
  const seen = new Set();

  if (lead) {
    people.push(lead);
    if (lead.id) seen.add(lead.id);
  }
  for (const raw of participants) {
    const person = toPerson(raw, { isLead: false });
    if (!person) continue;
    // A participant row that repeats the lead is the same human being
    // twice, not a second member.
    if (person.id && seen.has(person.id)) continue;
    if (person.id) seen.add(person.id);
    people.push(person);
  }

  const total = people.length;
  const kind = total === 0 ? "open" : total > 1 ? "team" : "person";

  return {
    people,
    lead: lead || people[0] || null,
    total,
    kind,
    visible: people.slice(0, VISIBLE_PEOPLE_LIMIT),
    overflowCount: Math.max(0, total - VISIBLE_PEOPLE_LIMIT),
  };
}

/** "+ 2 weitere", or `null` when everyone is already named. */
export function overflowLabel(overflowCount) {
  if (!overflowCount) return null;
  return `+ ${overflowCount} weitere`;
}

/** Two initials for an avatar. Never an id, never more than two glyphs. */
export function personInitials(person) {
  const label = (person?.label || "").trim();
  if (!label || label === "—") return "?";
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}
